import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { CalendarIcon, ArrowRight, ArrowLeft, Truck, Shield, CheckCircle } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const teslaModels = [
  'Model S', 'Model 3', 'Model X', 'Model Y', 'Cybertruck', 'Roadster', 'Semi'
];

const years = Array.from({ length: 15 }, (_, i) => 2026 - i);

const countries = [
  { name: 'United States', code: '+1',   region: 'US', flag: '🇺🇸' },
  { name: 'Canada',        code: '+1',   region: 'CA', flag: '🇨🇦' },
  { name: 'United Arab Emirates', code: '+971', region: 'AE', flag: '🇦🇪' },
  { name: 'Saudi Arabia',  code: '+966', region: 'SA', flag: '🇸🇦' },
  { name: 'Mexico',        code: '+52',  region: 'MX', flag: '🇲🇽' },
  { name: 'India',         code: '+91',  region: 'IN', flag: '🇮🇳' },
  { name: 'United Kingdom',code: '+44',  region: 'GB', flag: '🇬🇧' },
  { name: 'Germany',       code: '+49',  region: 'DE', flag: '🇩🇪' },
  { name: 'France',        code: '+33',  region: 'FR', flag: '🇫🇷' },
  { name: 'Spain',         code: '+34',  region: 'ES', flag: '🇪🇸' },
  { name: 'Italy',         code: '+39',  region: 'IT', flag: '🇮🇹' },
  { name: 'Netherlands',   code: '+31',  region: 'NL', flag: '🇳🇱' },
  { name: 'Belgium',       code: '+32',  region: 'BE', flag: '🇧🇪' },
  { name: 'Switzerland',   code: '+41',  region: 'CH', flag: '🇨🇭' },
  { name: 'Austria',       code: '+43',  region: 'AT', flag: '🇦🇹' },
  { name: 'Denmark',       code: '+45',  region: 'DK', flag: '🇩🇰' },
  { name: 'Sweden',        code: '+46',  region: 'SE', flag: '🇸🇪' },
  { name: 'Norway',        code: '+47',  region: 'NO', flag: '🇳🇴' },
  { name: 'Finland',       code: '+358', region: 'FI', flag: '🇫🇮' },
  { name: 'Poland',        code: '+48',  region: 'PL', flag: '🇵🇱' },
  { name: 'Czech Republic',code: '+420', region: 'CZ', flag: '🇨🇿' },
  { name: 'Greece',        code: '+30',  region: 'GR', flag: '🇬🇷' },
  { name: 'Portugal',      code: '+351', region: 'PT', flag: '🇵🇹' },
  { name: 'Ireland',       code: '+353', region: 'IE', flag: '🇮🇪' },
  { name: 'Australia',     code: '+61',  region: 'AU', flag: '🇦🇺' },
  { name: 'New Zealand',   code: '+64',  region: 'NZ', flag: '🇳🇿' },
  { name: 'Japan',         code: '+81',  region: 'JP', flag: '🇯🇵' },
  { name: 'South Korea',   code: '+82',  region: 'KR', flag: '🇰🇷' },
  { name: 'China',         code: '+86',  region: 'CN', flag: '🇨🇳' },
  { name: 'Singapore',     code: '+65',  region: 'SG', flag: '🇸🇬' },
  { name: 'Hong Kong',     code: '+852', region: 'HK', flag: '🇭🇰' },
  { name: 'Thailand',      code: '+66',  region: 'TH', flag: '🇹🇭' },
  { name: 'Malaysia',      code: '+60',  region: 'MY', flag: '🇲🇾' },
  { name: 'Indonesia',     code: '+62',  region: 'ID', flag: '🇮🇩' },
  { name: 'Philippines',   code: '+63',  region: 'PH', flag: '🇵🇭' },
  { name: 'Vietnam',       code: '+84',  region: 'VN', flag: '🇻🇳' },
  { name: 'Brazil',        code: '+55',  region: 'BR', flag: '🇧🇷' },
  { name: 'Argentina',     code: '+54',  region: 'AR', flag: '🇦🇷' },
  { name: 'Chile',         code: '+56',  region: 'CL', flag: '🇨🇱' },
  { name: 'Colombia',      code: '+57',  region: 'CO', flag: '🇨🇴' },
  { name: 'Peru',          code: '+51',  region: 'PE', flag: '🇵🇪' },
  { name: 'South Africa',  code: '+27',  region: 'ZA', flag: '🇿🇦' },
  { name: 'Egypt',         code: '+20',  region: 'EG', flag: '🇪🇬' },
];

export default function QuotePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    make: 'Tesla',
    model: '',
    year: '',
    condition: 'running',
    pickup_zip: '',
    pickup_address_type: 'residential',
    delivery_zip: '',
    delivery_address_type: 'residential',
    transport_type: 'open',
    pickup_window_start: addDays(new Date(), 4),   // ← auto-select first available
    pickup_window_end: addDays(new Date(), 10),
    name: '',
    countryCode: '+1',
    phone: '',
    email: '',
    notes: ''
  });

  const non_serviced_zips = (process.env.REACT_APP_NON_SERVICED_ZIPS || "").split(",");
  
  function isZipNonServiced(startZip, endZip) {
    console.log(non_serviced_zips);
    console.log("Checking serviceability for:", startZip, endZip);
    // 1. Get the 3-digit prefix for the pickup and delivery points
    const pickupPrefix = String(startZip).trim().substring(0, 3);
    const deliveryPrefix = String(endZip).trim().substring(0, 3);

    // 2. If EITHER the pickup OR delivery prefix is in the unserviced list, block it.
    if (non_serviced_zips.includes(pickupPrefix) || non_serviced_zips.includes(deliveryPrefix)) {
        return true; 
    }

    return false; 
}

  const updateForm = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.model || !formData.year) {
          toast.error('Please select your vehicle model and year');
          return false;
        }
        // --- PREVENT PROGRESS IF INOPERABLE ---
        if (formData.condition === 'inoperable') {
          toast.error("We currently don't transport inoperable vehicles. Please select 'Running' to continue.");
          return false;
        }
        return true;
      case 2:
        if (!formData.pickup_zip || !formData.delivery_zip) {
          toast.error('Please enter both pickup and delivery zip codes');
          return false;
        }
        if (formData.pickup_zip.length !== 5 || formData.delivery_zip.length !== 5) {
          toast.error('Please enter valid 5-digit zip codes');
          return false;
        }
        // --- NEW VALIDATION ADDED HERE ---
        if (formData.pickup_zip === formData.delivery_zip) {
          toast.error('Pickup and delivery zip codes cannot be the same');
          return false;
        }
        if (isZipNonServiced(formData.pickup_zip, formData.delivery_zip)) {
          toast.error('We currently do not provide service in this area.');
          return false;
        }
        // ---------------------------------
        return true;
      case 3:
        if (!formData.pickup_window_start || !formData.pickup_window_end) {
          toast.error('Please select your pickup window');
          return false;
        }
        return true;
      case 4:
        if (!formData.name || !formData.countryCode || !formData.phone || !formData.email) {
          toast.error('Please fill in all contact details');
          return false;
        }
        if (!formData.email.includes('@')) {
          toast.error('Please enter a valid email address');
          return false;
        }
        return true;
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (validateStep()) {
      setStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setStep(prev => prev - 1);
  };

  const submitQuote = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    try {
      const payload = {
        ...formData,
        year: parseInt(formData.year),
        phone: formData.countryCode + formData.phone,
        pickup_window_start: formData.pickup_window_start.toISOString(),
        pickup_window_end: formData.pickup_window_end.toISOString()
      };
      delete payload.countryCode;

      const response = await fetch(`${API_URL}/api/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create quote');
      }

      toast.success('Quote created successfully!');
      navigate(`/quote/${data.load_id}`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const minDate = addDays(new Date(), 3);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <main className="pt-24 pb-20">
        <div className="max-w-3xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="overline mb-4">Free Quote</p>
            <h1 className="font-outfit text-4xl md:text-5xl font-semibold tracking-tight mb-4">
              Get Your Instant Quote
            </h1>
            <p className="text-muted-foreground">
              Fill in your details below and receive a price in under 60 seconds.
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mb-12" data-testid="quote-progress">
            {[1, 2, 3, 4].map((s) => (
              <div key={s} className="flex items-center">
                <div 
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    s === step 
                      ? 'bg-accent text-accent-foreground' 
                      : s < step 
                        ? 'bg-primary text-primary-foreground' 
                        : 'bg-muted text-muted-foreground'
                  }`}
                >
                  {s < step ? <CheckCircle className="h-4 w-4" /> : s}
                </div>
                {s < 4 && (
                  <div className={`w-12 h-0.5 mx-2 ${s < step ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>

          {/* Form Card */}
          <div className="glass border border-white/10 rounded-2xl p-8" data-testid="quote-form">
            {/* Step 1: Vehicle */}
            {step === 1 && (
              <div className="space-y-6 animate-fade-in">
                <h2 className="font-outfit text-xl font-semibold mb-6">Vehicle Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Make</Label>
                    <Input 
                      value="Tesla" 
                      disabled 
                      className="bg-muted/50 border-border"
                      data-testid="quote-make-input"
                    />
                  </div>

                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Model</Label>
                    <Select value={formData.model} onValueChange={(v) => updateForm('model', v)}>
                      <SelectTrigger className="bg-transparent border-border" data-testid="quote-model-select">
                        <SelectValue placeholder="Select model" />
                      </SelectTrigger>
                      <SelectContent>
                        {teslaModels.map((model) => (
                          <SelectItem key={model} value={model}>{model}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Year</Label>
                    <Select value={formData.year} onValueChange={(v) => updateForm('year', v)}>
                      <SelectTrigger className="bg-transparent border-border" data-testid="quote-year-select">
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map((year) => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Condition</Label>
                    <RadioGroup 
                      value={formData.condition} 
                      onValueChange={(v) => updateForm('condition', v)}
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="running" id="running" data-testid="quote-condition-running" />
                        <Label htmlFor="running" className="cursor-pointer">Running</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="inoperable" id="inoperable" data-testid="quote-condition-inoperable" />
                        <Label htmlFor="inoperable" className="cursor-pointer text-muted-foreground">Inoperable</Label>
                      </div>
                    </RadioGroup>
                    {formData.condition === 'inoperable' && (
                      <p className="text-sm text-destructive mt-2">
                        Note: We currently don't transport inoperable vehicles.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Route */}
            {step === 2 && (
              <div className="space-y-6 animate-fade-in">
                <h2 className="font-outfit text-xl font-semibold mb-6">Route Details</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-accent">Pickup Location</h3>
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Zip Code</Label>
                      <Input 
                        placeholder="12345"
                        value={formData.pickup_zip}
                        onChange={(e) => updateForm('pickup_zip', e.target.value.replace(/\D/g, '').slice(0, 5))}
                        className="bg-transparent border-border"
                        data-testid="quote-pickup-zip"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Address Type</Label>
                      <RadioGroup 
                        value={formData.pickup_address_type} 
                        onValueChange={(v) => updateForm('pickup_address_type', v)}
                        className="flex gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="residential" id="pickup-res" />
                          <Label htmlFor="pickup-res" className="cursor-pointer">Residential</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="business" id="pickup-bus" />
                          <Label htmlFor="pickup-bus" className="cursor-pointer">Business</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="text-sm font-medium text-accent">Delivery Location</h3>
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Zip Code</Label>
                      <Input 
                        placeholder="90210"
                        value={formData.delivery_zip}
                        onChange={(e) => updateForm('delivery_zip', e.target.value.replace(/\D/g, '').slice(0, 5))}
                        className="bg-transparent border-border"
                        data-testid="quote-delivery-zip"
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-muted-foreground mb-2 block">Address Type</Label>
                      <RadioGroup 
                        value={formData.delivery_address_type} 
                        onValueChange={(v) => updateForm('delivery_address_type', v)}
                        className="flex gap-4"
                      >
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="residential" id="delivery-res" />
                          <Label htmlFor="delivery-res" className="cursor-pointer">Residential</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                          <RadioGroupItem value="business" id="delivery-bus" />
                          <Label htmlFor="delivery-bus" className="cursor-pointer">Business</Label>
                        </div>
                      </RadioGroup>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Label className="text-sm text-muted-foreground mb-2 block">Transport Type</Label>
                  <RadioGroup 
                    value={formData.transport_type} 
                    onValueChange={(v) => updateForm('transport_type', v)}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4"
                  >
                    <label 
                      htmlFor="transport-open"
                      className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                        formData.transport_type === 'open' 
                          ? 'border-accent bg-accent/10' 
                          : 'border-border hover:border-white/20'
                      }`}
                    >
                      <RadioGroupItem value="open" id="transport-open" className="mt-1" data-testid="quote-transport-open" />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Truck className="h-4 w-4" />
                          <span className="font-medium">Open Carrier</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Cost-effective option. Your Tesla is transported on an open trailer.
                        </p>
                        <p className="text-sm font-medium text-accent mt-2">$199 deposit</p>
                      </div>
                    </label>

                    <label 
                      htmlFor="transport-enclosed"
                      className={`flex items-start gap-4 p-4 rounded-xl border cursor-pointer transition-all ${
                        formData.transport_type === 'enclosed' 
                          ? 'border-accent bg-accent/10' 
                          : 'border-border hover:border-white/20'
                      }`}
                    >
                      <RadioGroupItem value="enclosed" id="transport-enclosed" className="mt-1" data-testid="quote-transport-enclosed" />
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Shield className="h-4 w-4" />
                          <span className="font-medium">Enclosed Carrier</span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Premium protection. Your Tesla travels in a fully enclosed trailer.
                        </p>
                        <p className="text-sm font-medium text-accent mt-2">$299 deposit</p>
                      </div>
                    </label>
                  </RadioGroup>
                </div>
              </div>
            )}

            {/* Step 3: Dates */}
            {step === 3 && (
              <div className="space-y-6 animate-fade-in">
                <h2 className="font-outfit text-xl font-semibold mb-6">Pickup Window</h2>
                <p className="text-sm text-muted-foreground mb-4">
                  Select your preferred pickup date range. Must be at least 72 hours from now.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">First Available Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal bg-transparent border-border"
                          data-testid="quote-pickup-start-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.pickup_window_start 
                            ? format(formData.pickup_window_start, 'PPP')
                            : 'Select date'
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.pickup_window_start}
                          onSelect={(date) => {
                            updateForm('pickup_window_start', date);
                            if (!formData.pickup_window_end || formData.pickup_window_end <= date) {
                              updateForm('pickup_window_end', addDays(date, 7));
                            }
                          }}
                          disabled={(date) => date < minDate}
                          defaultMonth={minDate}   // ← opens on May instead of April
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Last Available Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal bg-transparent border-border"
                          data-testid="quote-pickup-end-btn"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {formData.pickup_window_end 
                            ? format(formData.pickup_window_end, 'PPP')
                            : 'Select date'
                          }
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={formData.pickup_window_end}
                          onSelect={(date) => updateForm('pickup_window_end', date)}
                          disabled={(date) =>
                            date < minDate ||
                            (formData.pickup_window_start && date < formData.pickup_window_start)
                          }
                          defaultMonth={formData.pickup_window_end ?? minDate}   // ← follows selected end date, falls back to minDate
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Special Instructions (Optional)</Label>
                  <Textarea 
                    placeholder="Any special requirements? e.g., gated community, low clearance garage..."
                    value={formData.notes}
                    onChange={(e) => updateForm('notes', e.target.value)}
                    className="bg-transparent border-border min-h-[100px]"
                    data-testid="quote-notes"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Contact */}
            {step === 4 && (
              <div className="space-y-6 animate-fade-in">
                <h2 className="font-outfit text-xl font-semibold mb-6">Contact Information</h2>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Full Name</Label>
                    <Input 
                      placeholder="John Smith"
                      value={formData.name}
                      onChange={(e) => updateForm('name', e.target.value)}
                      className="bg-transparent border-border"
                      data-testid="quote-name"
                    />
                  </div>

                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Phone Number</Label>
                    <div className="flex gap-2">
                      <Select value={formData.countryCode} onValueChange={(v) => updateForm('countryCode', v)}>
                        <SelectTrigger className="bg-transparent border-border w-28 shrink-0" data-testid="quote-country-code-select">
                          <span className="flex items-center gap-1 truncate">
                            {(() => {
                              const sel = countries.find(c => c.code === formData.countryCode);
                              return sel ? <>{sel.flag} {sel.code}</> : formData.countryCode;
                            })()}
                          </span>
                        </SelectTrigger>
                        <SelectContent>
                          {countries.map((country) => (
                            <SelectItem key={`${country.region}-${country.code}`} value={country.code}>
                              {country.flag} {country.code} — {country.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="5551234567"
                        value={formData.phone}
                        onChange={(e) => updateForm('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        maxLength="10"
                        className="bg-transparent border-border flex-1"
                        data-testid="quote-phone"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-sm text-muted-foreground mb-2 block">Email Address</Label>
                    <Input 
                      type="email"
                      placeholder="john@example.com"
                      value={formData.email}
                      onChange={(e) => updateForm('email', e.target.value)}
                      className="bg-transparent border-border"
                      data-testid="quote-email"
                    />
                  </div>
                </div>

                {/* Summary */}
                <div className="mt-8 p-4 bg-muted/30 rounded-xl">
                  <h3 className="text-sm font-medium mb-4">Quote Summary</h3>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <span className="text-muted-foreground">Vehicle:</span>
                    <span>{formData.year} {formData.make} {formData.model}</span>
                    <span className="text-muted-foreground">Route:</span>
                    <span>{formData.pickup_zip} → {formData.delivery_zip}</span>
                    <span className="text-muted-foreground">Transport:</span>
                    <span className="capitalize">{formData.transport_type}</span>
                    <span className="text-muted-foreground">Pickup Window:</span>
                    <span>
                      {formData.pickup_window_start && formData.pickup_window_end
                        ? `${format(formData.pickup_window_start, 'MMM d')} - ${format(formData.pickup_window_end, 'MMM d')}`
                        : 'Not selected'
                      }
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
              {step > 1 ? (
                <Button 
                  variant="outline" 
                  onClick={prevStep}
                  className="border-border hover:bg-muted hover:text-gray-900 dark:hover:text-white"
                  data-testid="quote-prev-btn"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Back
                </Button>
              ) : (
                <div />
              )}

              {step < 4 ? (
                <Button 
                  onClick={nextStep}
                  className="bg-primary text-primary-foreground hover:bg-white/90 hover:text-gray-900"
                  data-testid="quote-next-btn"
                >
                  Continue
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button 
                  onClick={submitQuote}
                  disabled={loading}
                  className="bg-accent text-accent-foreground hover:bg-accent/90"
                  data-testid="quote-submit-btn"
                >
                  {loading ? 'Getting Quote...' : 'Get My Quote'}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
